import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authService } from '../../services/authService';
import { Link } from 'react-router-dom';
import { DSSheet, DSButton, DSIcon } from '../../design-system/primitives';
import { getJson, scorekeepingUrl } from './scorekeepingApi';
import PublicHeader from './PublicHeader';
import { formatScore, ordinal, daysLeftInMonth } from './format';
import '../../design-system/badges/ds-achievement-badge.css';
import '../../design-system/badges/ds-badge-shelf.css';
import '../../design-system/badges/ds-placement-medal.css';
import MonthlyTrophyArt from '../../design-system/badges/MonthlyTrophyArt';
import './scorekeeping.css';
import './leaderboard.css';

/**
 * P5 and M1. The monthly competition, public, with more for a member.
 *
 * The club cares about competition and cares more about being accessible to
 * people who do not think they are good enough to start. That is not a slogan
 * here, it is the layout.
 *
 * What a signed-in member sees first is a card about them: where they are, and
 * the two things actually within their reach this month. Their rank is in it,
 * plainly, because hiding it would be patronising. What is deliberately absent
 * anywhere on this page is a distance-to-leader figure. For most of the club
 * that number is enormous, unchanging and discouraging, and it is exactly the
 * comparison that stops people entering at all.
 *
 * Jump to me exists because the card cannot show the rows around them, and a
 * member in 48th should not have to scroll past forty-seven people to find
 * themselves.
 */

/**
 * Medals, not the badge component's rarity scale.
 *
 * Gold, silver and bronze need no legend. The rarity scale renders second place
 * in pink and third in purple, which is legible only to somebody who has been
 * told the scale first. See design-system/badges/ds-placement-medal.css.
 */
const MEDAL = (position) => `ds-achbadge--medal-${position}`;

function Leaderboard() {
  const [months, setMonths] = useState([]);
  const [period, setPeriod] = useState(null);
  const [board, setBoard] = useState(null);
  const [mine, setMine] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [game, setGame] = useState(null);
  const [explainerOpen, setExplainerOpen] = useState(false);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [error, setError] = useState(null);

  const myRowRef = useRef(null);
  // authService.getCurrentUser() parses localStorage and hands back a fresh object
  // every call, so it has a new identity on every render. Read once and hold it:
  // anything that takes it as a dependency otherwise rebuilds on every render, and
  // a fetch keyed on that turns into an unbounded request loop.
  const currentUser = useMemo(() => authService.getCurrentUser(), []);
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    getJson('/months').then(setMonths).catch(() => setMonths([]));
  }, []);

  const load = useCallback(async (which) => {
    try {
      const data = await getJson(`/standings/${which || 'current'}`);
      setBoard(data);
      setPeriod(data.period);
      setError(null);

      if (currentUser) {
        getJson(`/me/standing/${data.period}`).then(setMine).catch(() => setMine(null));
      }
    } catch (err) {
      setError(err.message);
    }
  }, [currentUser]);

  useEffect(() => { load('current'); }, [load]);

  /**
   * Open one score: the photo that backs it, and the board it sits in.
   *
   * Tapping a machine used to navigate to that machine's page, which threw away
   * the board being read and lost the player whose row was tapped. What somebody
   * actually wants here is to see the score, so it opens in place over the top
   * and closes back onto the row they came from.
   *
   * One request does all of it. The machine board already carries each entry's
   * photo, rank and points, so there is nothing to compute on this side and
   * nothing to fetch twice.
   */
  const openGame = async (player, machine) => {
    setGame({ player, machine, loading: true, board: null, error: null });
    try {
      const data = await getJson(`/machines/${machine.machineId}/board?period=${period}`);
      setGame((current) => (
        current && current.machine.machineId === machine.machineId
          ? { ...current, loading: false, board: data.entries || [] }
          : current
      ));
    } catch (err) {
      setGame((current) => (
        current && current.machine.machineId === machine.machineId
          ? { ...current, loading: false, error: err.message }
          : current
      ));
    }
  };

  /** Is this board row the player whose score we opened? */
  const isTheirRow = (entry, player) => (
    player.userId
      ? entry.userId === player.userId
      : String(entry.playerName || '').toLowerCase() === String(player.playerName || '').toLowerCase()
  );

  const jumpToMe = () => {
    myRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const key = myRowKey();
    if (key) setExpanded(key);
  };

  const myRowKey = () => {
    if (!board || !currentUser) return null;
    const row = board.standings.find((p) => p.userId === currentUser.id);
    return row ? `${row.position}-${row.playerName}` : null;
  };

  if (error) {
    return (
      <div className="sk-page"><div className="sk-shell">
        <h1 className="sk-h1">Leaderboard unavailable</h1>
        <p className="sk-muted">{error}</p>
      </div></div>
    );
  }

  if (!board) return <div className="sk-page"><div className="sk-shell sk-muted">Loading…</div></div>;

  const settled = board.status === 'settled';
  const meKey = myRowKey();
  const myPosition = mine?.onTheBoard ? mine.position : null;
  // Only worth offering when they are far enough down to be a scroll away.
  const offerJump = Boolean(meKey) && myPosition > 6;

  return (
    <div className="sk-page">
      {!currentUser && <PublicHeader signInRedirect="/scorekeeping/leaderboard" />}

      <div className="sk-shell">
        <div className="lb-head">
          {/* Title and the way out of it on one line. A row of month chips was
              fine with two months and becomes a horizontal scroll with a year of
              them, so the wireframe spends one button instead. */}
          <div className="lb-head__top">
            <h1 className="sk-h1">{board.label}</h1>
            <div className="lb-head__actions">
              {months.length > 1 && (
                <button
                  type="button"
                  className="lb-changemonth"
                  aria-expanded={monthPickerOpen}
                  onClick={() => setMonthPickerOpen(!monthPickerOpen)}
                >
                  Change month
                </button>
              )}
              {/* The way into the admin screens, for the people who have them.
                  It lives here rather than in the sidebar because this is the
                  page an admin is looking at when they decide something needs
                  changing, and it keeps a tool nobody else can use out of
                  everyone else's navigation. */}
              {isAdmin && (
                <Link className="lb-adminlink" to="/scorekeeping/admin">
                  <DSIcon name="medal" />
                  Manage
                </Link>
              )}
            </div>
          </div>

          {/* Facts about the month, not about you. The best-of rule is here
              because every number below it is meaningless without it. */}
          <div className="lb-chips">
            {/* Whether the month is still running, and the rule every total
                below it is computed under. The date a month was settled is
                bookkeeping, and the player count is already the length of the
                list underneath. */}
            <span className={`lb-chip ${settled ? '' : 'is-live'}`}>
              {settled ? 'Final' : `Live · ${daysLeftInMonth()} days left`}
            </span>
            <span className="lb-chip">
              Best {board.countedMachineLimit} of {board.countingMachineCount} machines
            </span>
          </div>

          {monthPickerOpen && (
            <div className="lb-months" role="group" aria-label="Competition month">
              {months.map((m) => (
                <button
                  key={m.period}
                  type="button"
                  className={`lb-month ${m.period === period ? 'is-selected' : ''}`}
                  aria-pressed={m.period === period}
                  onClick={() => {
                    setExpanded(null);
                    setMonthPickerOpen(false);
                    load(m.period);
                  }}
                >
                  {m.label}
                  {m.isCurrent && <span className="lb-month__live">Live</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* The real badge tiles, not a stand-in for them. These are the thing
            the club hands out for a placement, so the board shows the same
            artwork somebody will have on their profile rather than a label that
            merely says which one they got. */}
        {settled && board.standings.length > 0 && (
          <section className="ds-badgeshelf ds-badgeshelf--plain lb-shelf">
            <div className="ds-badgeshelf__head">
              <p className="ds-badgeshelf__title">Badges awarded</p>
              <span className="ds-badgeshelf__tally">{board.label}</span>
            </div>
            <div className="ds-badgeshelf__rail">
              {board.standings.slice(0, 4).map((p) => (
                <div className="lb-shelf__tile" key={`${p.position}-${p.playerName}`}>
                  <figure
                    className={`ds-achbadge ${MEDAL(p.position)}`}
                    aria-label={`${ordinal(p.position)} place, ${board.label}`}
                  >
                    <span className="ds-achbadge__frame">
                      <span className="ds-achbadge__inner" />
                      <span className="ds-achbadge__art">
                        <MonthlyTrophyArt />
                      </span>
                    </span>
                    {/* The ribbon carries the placement, not the month. The art
                        took the slot the placement used to sit in, and the month
                        is already at the top of this shelf. It is also the only
                        thing short enough to stay legible: the ribbon scales with
                        the badge, and "AUGUST 2026" at four-across on a phone was
                        six pixels tall. */}
                    <figcaption className="ds-achbadge__ribbon">{ordinal(p.position)}</figcaption>
                  </figure>
                  <p className="lb-shelf__who">{p.displayName || p.playerName}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Where you are. The rank is in it, and nothing compares you to the top. */}
        {mine?.onTheBoard && (
          <section className="lb-you">
            <div className="lb-you__top">
              <div>
                <p className="sk-eyebrow">Where you are</p>
                <p className="lb-you__rank">
                  {ordinal(mine.position)} <span>of {mine.playerCount}</span>
                  {/* Sits on the rank's own line rather than under the card. It
                      is a convenience, not a decision, and as a full-width block
                      it was reading as the card's primary action while costing
                      more height than the rank it helps you find. */}
                  {offerJump && (
                    <button type="button" className="lb-jump" onClick={jumpToMe}>
                      Jump to me
                    </button>
                  )}
                </p>
              </div>
              <div className="lb-you__points">
                <span className="sk-eyebrow">Points</span>
                <strong>{formatScore(mine.totalPoints)}</strong>
              </div>
            </div>

            {mine.previous && (
              <p className="lb-you__trend">
                {mine.previous.position === mine.position
                  ? `Same as ${mine.previous.label}.`
                  : mine.previous.position > mine.position
                    ? `Up from ${ordinal(mine.previous.position)} in ${mine.previous.label.split(' ')[0]}.`
                    : `Down from ${ordinal(mine.previous.position)} in ${mine.previous.label.split(' ')[0]}.`}
              </p>
            )}
            {mine.isFirstMonth && (
              <p className="lb-you__trend">
                Your first month, {mine.machinesCounting} machine
                {mine.machinesCounting === 1 ? '' : 's'} in. Every one you add counts.
              </p>
            )}

            {mine.withinReach.length > 0 && !settled && (
              <div className="lb-reach">
                <p className="sk-eyebrow">What&apos;s within reach</p>
                {/* Each one names a machine, so each one goes to it. */}
                {mine.withinReach.map((item) => (
                  <Link
                    className="lb-reach__item"
                    key={`${item.kind}-${item.machineId}`}
                    to={`/scorekeeping/machines/${item.machineId}`}
                  >
                    <div className="lb-reach__body">
                      <p className="lb-reach__title">{item.title}</p>
                      <p className="sk-muted">{item.detail}</p>
                    </div>
                    <span className="lb-reach__gain">+{formatScore(item.gain)}</span>
                  </Link>
                ))}
              </div>
            )}

          </section>
        )}

        <button
          type="button"
          className="lb-explainer"
          onClick={() => setExplainerOpen(!explainerOpen)}
          aria-expanded={explainerOpen}
        >
          How it works
        </button>
        {explainerOpen && (
          <p className="sk-muted lb-explainer__body">
            Your total is your best {board.countedMachineLimit} machines. First place on a machine is
            100 points, second 90, third 85, then one less each place.
          </p>
        )}

        {board.standings.length === 0 ? (
          <div className="sk-card sk-empty">
            <p className="sk-empty__title">Nothing on the board yet</p>
            <p className="sk-muted">A new month starts empty. Everyone is level, including you.</p>
          </div>
        ) : (
          <div className="lb-list">
            {board.standings.map((player) => {
              const key = `${player.position}-${player.playerName}`;
              const isMe = Boolean(currentUser) && player.userId === currentUser.id;
              const open = expanded === key;
              const counting = (player.breakdown || []).filter((m) => m.counting);
              const other = (player.breakdown || []).filter((m) => !m.counting);

              return (
                <section
                  className={`lb-row ${isMe ? 'is-me' : ''} ${player.position <= 4 ? `is-medal-${player.position}` : ''}`}
                  key={key}
                  ref={isMe ? myRowRef : null}
                >
                  <button
                    type="button"
                    className="lb-row__head"
                    onClick={() => setExpanded(open ? null : key)}
                    aria-expanded={open}
                  >
                    <span className="lb-row__rank">{ordinal(player.position)}</span>
                    <span className="lb-row__who">
                      {/* The person leads, their Stern handle sits under it. A
                          column of handles is not how members recognise each
                          other, and a guest has only ever given us one name. */}
                      <span className="lb-row__name">
                        {isMe ? 'You' : (player.displayName || player.playerName)}
                      </span>
                      <span className="lb-row__meta">
                        {player.isGuest ? 'Guest' : `@${player.playerName}`}
                        {' · '}{player.machinesPlayed} machine{player.machinesPlayed === 1 ? '' : 's'}
                      </span>
                    </span>
                    <span className="lb-row__points">{formatScore(player.totalPoints)}</span>
                  </button>

                  {open && (
                    <div className="lb-detail">
                      <p className="lb-detail__label">
                        Counting · {counting.length} of a possible {board.countedMachineLimit}
                      </p>
                      {/* All of them. "Show the other 16" was a second tap to
                          reach the thing the first tap was asking for. */}
                      {counting.map((m) => (
                        <button
                          type="button"
                          className="lb-game"
                          key={`${m.machineId}-${m.score}`}
                          onClick={() => openGame(player, m)}
                        >
                          <span className="lb-game__rank">{ordinal(m.rank)}</span>
                          <span className="lb-game__machine">{m.machineName}</span>
                          <span className="lb-game__score">{formatScore(m.score)}</span>
                          <span className="lb-game__points">+{m.points}</span>
                        </button>
                      ))}

                      {/* Not a second flat list. These earned nothing, and why is
                          the useful part: either the machine is out of the
                          competition or it fell outside their best N. */}
                      {other.length > 0 && (
                        <>
                          <p className="lb-detail__label lb-detail__label--muted">Played, but not counting</p>
                          {other.map((m) => (
                            <button
                              type="button"
                              className="lb-game lb-game--muted"
                              key={`${m.machineId}-${m.score}`}
                              onClick={() => openGame(player, m)}
                            >
                              <span className="lb-game__rank">{ordinal(m.rank)}</span>
                              <span className="lb-game__machine">{m.machineName}</span>
                              <span className="lb-game__score">{formatScore(m.score)}</span>
                              <span className="lb-game__why">
                                {m.counts ? 'Outside best ' + board.countedMachineLimit : 'Machine excluded'}
                              </span>
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      {game && (
        <DSSheet
          title={game.player.displayName || game.player.playerName}
          onClose={() => setGame(null)}
          footer={<DSButton variant="secondary" onClick={() => setGame(null)}>Close</DSButton>}
        >
          {!game.player.isGuest && game.player.displayName && (
            <p className="lb-sheet__handle">@{game.player.playerName}</p>
          )}

          <p className="lb-sheet__machine">{game.machine.machineName}</p>

          <div className="lb-sheet__stats">
            <span className="lb-sheet__stat">
              <span className="lb-sheet__statLabel">Score</span>
              <strong>{formatScore(game.machine.score)}</strong>
            </span>
            <span className="lb-sheet__stat">
              <span className="lb-sheet__statLabel">On this machine</span>
              <strong>{ordinal(game.machine.rank)}</strong>
            </span>
            <span className="lb-sheet__stat">
              <span className="lb-sheet__statLabel">Points</span>
              <strong>{game.machine.counting ? game.machine.points : '\u2014'}</strong>
            </span>
          </div>

          {/* The photo, when the club has one. A Stern score never had one, and
              older scores may have none, so every state here is ordinary rather
              than an error. */}
          {(() => {
            if (game.loading) return <p className="sk-muted">Loading…</p>;
            if (game.error) return <p className="sk-muted">{game.error}</p>;

            const mine = (game.board || []).find((entry) => isTheirRow(entry, game.player));
            if (!mine) return null;

            // A photo that fails to load is not an error worth a message: the
            // image simply takes itself off the page.
            const photo = mine.photoPath
              ? scorekeepingUrl(`/photos/${mine.photoPath}`)
              : null;

            return (
              <>
                {photo ? (
                  <img
                    className="lb-sheet__photo"
                    src={photo}
                    alt={`${game.machine.machineName} score`}
                    onError={(event) => { event.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <p className="sk-muted">
                    {mine.source === 'stern'
                      ? 'Recorded by the machine, so there is no photo to show.'
                      : 'No photo on this one.'}
                  </p>
                )}

                <p className="lb-sheet__label">{game.machine.machineName} board</p>
                <table className="sk-board">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Player</th>
                      <th className="sk-num">Score</th>
                      <th className="sk-num">Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {game.board.map((entry) => (
                      <tr
                        key={`${entry.rank}-${entry.playerName}`}
                        className={isTheirRow(entry, game.player) ? 'is-me' : ''}
                      >
                        <td>{entry.rank}</td>
                        <td className="sk-board__player">{entry.playerName}</td>
                        <td className="sk-num">{formatScore(entry.score)}</td>
                        <td className="sk-num sk-board__points">{entry.points || '\u2014'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            );
          })()}
        </DSSheet>
      )}
    </div>
  );
}

export default Leaderboard;
